// src/services/apiClient.ts
/**
 * API Client
 */


import { VALIDATION_MESSAGES } from "../constants/validationConstants";

/**
 * Fetch wrapper without authentication
 */
export const apiClient = async <T = any>(
  ApiUrl: string,
  options: RequestInit = {}
): Promise<T> => {
  const { headers = {}, method, body, ...restOptions } = options;

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  };

  // Build full URL
  const url = ApiUrl;


  try {
    const response = await fetch(url, {
      ...restOptions,
      method: method || (body ? 'POST' : 'GET'),
      headers: requestHeaders,
      credentials: 'include', // Include cookies for internal authentication
      body: body as BodyInit | null,
    } as RequestInit);

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));

      // Log basic details
      // Prefer validation errors if present
      const hasServerValidationErrors = Array.isArray((errorData as any)?.validationErrors);
      const serverValidationMessage = hasServerValidationErrors
        ? ((errorData as any).validationErrors as unknown[])
          .map((m) => (typeof m === 'string' ? m : JSON.stringify(m)))
          .join(' & ')
        : null;

      // Include status code in error message for better error handling
      const baseErrorMessage = serverValidationMessage || VALIDATION_MESSAGES.API_ERROR_GENERIC;
      const errorMessage = `${baseErrorMessage} (Status: ${response.status})`;

      throw new Error(errorMessage);
    }

    // Return parsed JSON
    return await response.json();
  } catch (error) {
    // Enhance network/offline errors
    const isOffline = (typeof navigator !== 'undefined' && navigator && 'onLine' in navigator && !navigator.onLine);

    if (isOffline) {
      throw new Error(VALIDATION_MESSAGES.API_ERROR_GENERIC);
    }

    throw error;
  }
};

/**
 * Convenience methods for common HTTP verbs
 */
export const apiGet = <T = any>(endpoint: string, options?: RequestInit): Promise<T> => {
  return apiClient<T>(endpoint, { ...options, method: 'GET' });
};

export const apiPost = <T = any>(ApiUrl: string, data?: any, options?: RequestInit): Promise<T> => {
  return apiClient<T>(ApiUrl, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : null,
  } as RequestInit);
};
